<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 sm:p-6 lg:p-8">
    <!-- Top Right Theme Toggle Button -->
    <div class="absolute top-4 right-4 z-20">
      <button
        type="button"
        class="flex items-center px-3 py-2 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow focus:outline-none focus:ring-2 focus:ring-indigo-500"
        @click="toggleTheme"
      >
        <component :is="isDarkMode ? Sun : Moon" class="h-5 w-5" />
      </button>
    </div>
    <div class="card w-full max-w-md space-y-6 sm:space-y-8 p-4 sm:p-6 lg:p-8">
      <!-- Logo/Brand Section -->
      <div class="text-center">
        <div class="mx-auto text-primary dark:text-primary-dark">
          <img
            src="@/assets/main-logo1x1.png"
            alt="Logo"
            class="h-full w-full object-contain block dark:hidden"
          />
          <img
            src="@/assets/main-logo1x1-dark.png"
            alt="Logo Dark"
            class="h-full w-full object-contain hidden dark:block"
          />
        </div>
        <!-- <h2 class="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-primary-text dark:text-primary-dark-text">
          {{ appTitle }}
        </h2> -->

        <!-- Success Message -->
        <div v-if="successMessage" class="mt-4 rounded-md bg-success-bg dark:bg-success-dark-bg p-3 sm:p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <CheckCircle class="h-5 w-5 text-success dark:text-success-dark" />
            </div>
            <div class="ml-3">
              <p class="text-sm text-success-text dark:text-success-dark-text">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Error Message - Uncomment Code if you are not using Toast -->
        <!-- <div v-if="error" class="mt-4 rounded-md bg-danger-bg dark:bg-danger-dark-bg p-3 sm:p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-danger dark:text-danger-dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-danger-text dark:text-danger-dark-text">{{ error }}</p>
            </div>
          </div>
        </div> -->
      </div>

      <!-- Form Section -->
      <form class="mt-6 sm:mt-8 space-y-4 sm:space-y-6" @submit.prevent="handleLogin(rememberMe)">
        <div class="space-y-4">
          <div>
            <label for="email-address" class="label">
              Email address
            </label>
            <div class="mt-1">
              <input
                id="email-address"
                name="email"
                type="email"
                autocomplete="email"
                required
                v-model="email"
                class="input"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label for="password" class="label">
              Password
            </label>
            <div class="mt-1 relative">
              <input
                id="password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
                v-model="password"
                class="input pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <EyeOff v-if="showPassword" class="h-5 w-5" />
                <Eye v-else class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div class="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              v-model="rememberMe"
              class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label for="remember-me" class="ml-2 block text-sm text-primary-text dark:text-primary-dark-text">
              Remember me
            </label>
          </div>
          <!-- Uncomment if you want to add a "Forgot Password" link -->
          <!-- <div class="text-sm">
            <a href="#" class="font-medium text-primary hover:text-primary-hover dark:text-primary-dark dark:hover:text-primary-dark-hover">
              Forgot your password?
            </a>
          </div> -->
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="loading"
            class="btn btn-primary w-full"
          >
            {{ loading ? 'Signing in...' : 'Sign in' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useLogin } from '@/composables/auth/useLogin'
import { useThemeStore } from '@/stores/theme'
import { Sun, Moon, CheckCircle, Eye, EyeOff } from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Empathy Health Services, Inc.')

useTitle(`Login - ${appTitle.value}`)

const { email, password, loading, handleLogin } = useLogin()
const successMessage = ref('')
const rememberMe = ref(false)
const showPassword = ref(false)

// Theme logic (same as AppLayout)
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)
const toggleTheme = () => {
  themeStore.toggleTheme()
}
</script>